import { createContext, useContext, type ReactNode } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { useAuth } from './AuthContext'
import { useOrg } from './OrgContext'
import { invokeEdgeOrThrow, cloneCadenceConfig } from '@/lib/edge-functions'
import type { Cadence, Lead, CadenceStep, Template } from '@/types'

interface ExecuteStepParams {
  leadId: string
  stepId: string
  cadenceId: string
  message?: string
  subject?: string
  postUrl?: string
}

interface ExecuteStepResult {
  success: boolean
  alreadyConnected?: boolean
}

// A lead can belong to SEVERAL cadences (cadence_leads is many-to-many).
// The flattened cadence_id/current_step_id/status on Lead only reflect ONE
// membership — consumers that need the full picture must use cadence_memberships.
export interface CadenceMembership {
  cadence_id: string
  current_step_id: string | null
  status: string
}

export type LeadWithMemberships = Lead & { cadence_memberships?: CadenceMembership[] }

interface CadenceContextType {
  cadences: Cadence[]
  leads: LeadWithMemberships[]
  templates: Template[]
  isLoading: boolean
  createCadence: (name: string, description?: string, automationMode?: 'manual' | 'automated') => Promise<Cadence | null>
  // duplicateCadence is best-effort about the policy layer: the cadence + steps
  // always commit, but the Carlos-rubric / touch-angle / circuit-breaker clone
  // can fail independently. policyCloned surfaces that outcome so the caller can
  // warn the user instead of the failure being swallowed by a console.warn.
  duplicateCadence: (id: string) => Promise<(Cadence & { policyCloned: boolean }) | null>
  updateCadence: (id: string, data: Partial<Cadence>) => Promise<void>
  deleteCadence: (id: string) => Promise<void>
  createStep: (step: Omit<CadenceStep, 'id' | 'created_at' | 'updated_at' | 'org_id'>) => Promise<CadenceStep | null>
  updateStep: (id: string, data: Partial<CadenceStep>) => Promise<void>
  deleteStep: (id: string) => Promise<void>
  createLead: (lead: Omit<Lead, 'id' | 'created_at' | 'updated_at' | 'org_id'>) => Promise<Lead | null>
  updateLead: (id: string, data: Partial<Lead>) => Promise<void>
  deleteLead: (id: string) => Promise<void>
  // Return semantics (BUG-093):
  // - { alreadyInCadence: true,  reactivated: false } → membership with LIVE
  //   progress (active/scheduled, or paused mid-step) — nothing was touched.
  //   Also returned when a removed/completed membership has NO unsent step
  //   left (every step already went out — there is nothing to re-send).
  // - { alreadyInCadence: false, reactivated: true } → membership existed but
  //   was removed ('paused' + current_step_id null) or completed — restarted
  //   as 'active' at its first unsent step (+ schedule if automated).
  // - { alreadyInCadence: false, reactivated: false } → fresh assignment.
  assignLeadToCadence: (leadId: string, cadenceId: string, startingStepId?: string) => Promise<{ alreadyInCadence: boolean; reactivated: boolean }>
  removeLeadFromCadence: (leadId: string, cadenceId: string) => Promise<void>
  updateLeadCadenceStatus: (leadId: string, cadenceId: string, status: 'active' | 'paused') => Promise<void>
  createTemplate: (template: { name: string; step_type: string; subject_template?: string | null; body_template: string }) => Promise<Template | null>
  updateTemplate: (id: string, data: Partial<Template>) => Promise<void>
  deleteTemplate: (id: string) => Promise<void>
  executeStepForLead: (params: ExecuteStepParams) => Promise<ExecuteStepResult>
  markStepDoneForLead: (leadId: string, stepId: string, cadenceId: string) => Promise<void>
  moveLeadToNextStep: (leadId: string, cadenceId: string) => Promise<void>
  getLeadDayInCadence: (lead: Lead) => number
}

const CadenceContext = createContext<CadenceContextType | undefined>(undefined)

// Ensure user profile exists before operations that require it
async function ensureProfileExists(userId: string): Promise<void> {
  // Check if profile exists
  const { data: existingProfile } = await supabase
    .from('profiles')
    .select('user_id')
    .eq('user_id', userId)
    .single()

  if (!existingProfile) {
    // Create profile if it doesn't exist
    const { error } = await supabase
      .from('profiles')
      .insert({ user_id: userId, full_name: '' })

    if (error && error.code !== '23505') {
      // Ignore unique constraint violations (profile already exists)
      throw new Error(`Failed to create profile: ${error.message}`)
    }
  }
}

export function CadenceProvider({ children }: { children: ReactNode }) {
  const { user, session } = useAuth()
  const { orgId } = useOrg()
  const queryClient = useQueryClient()

  const { data: cadences = [], isLoading: cadencesLoading } = useQuery({
    queryKey: ['cadences', orgId, user?.id],
    queryFn: async () => {
      if (!user || !orgId) return []
      // Each user sees ONLY their own cadences — never mixed with other users in the same org
      const { data, error } = await supabase
        .from('cadences')
        .select('*, cadence_steps(*)')
        .eq('org_id', orgId)
        .eq('owner_id', user.id)
        .order('created_at', { ascending: false })
      if (error) throw error
      return (data || []).map((c: Record<string, unknown>) => ({
        ...c,
        steps: c.cadence_steps
      })) as Cadence[]
    },
    enabled: !!user && !!orgId,
  })

  const { data: leads = [], isLoading: leadsLoading } = useQuery({
    queryKey: ['leads', orgId, user?.id],
    queryFn: async () => {
      if (!user || !orgId) return []
      // Fetch leads with their cadence_leads relationship to get cadence info
      const { data, error } = await supabase
        .from('leads')
        .select('*, cadence_leads(cadence_id, current_step_id, status)')
        .eq('org_id', orgId)
        .eq('owner_id', user.id)
        .order('created_at', { ascending: false })
      if (error) throw error

      // Map the results to include cadence info at the lead level for UI compatibility
      return (data || []).map((lead) => {
        const leadData = lead as Record<string, unknown>
        const cadenceLeads = leadData.cadence_leads as Array<Record<string, unknown>> | null
        // Keep ALL memberships and prefer the active one for the flattened fields
        // (PostgREST row order is not deterministic, so [0] was arbitrary)
        const memberships: CadenceMembership[] = (cadenceLeads || []).map((cl) => ({
          cadence_id: cl.cadence_id as string,
          current_step_id: cl.current_step_id as string | null,
          status: cl.status as string,
        }))
        const cadenceLead = memberships.find((m) => m.status === 'active') ?? memberships[0] ?? null

        // Destructure to remove cadence_leads from the result
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { cadence_leads: _, ...leadWithoutRelation } = leadData

        return {
          ...leadWithoutRelation,
          cadence_id: cadenceLead?.cadence_id ?? null,
          current_step_id: cadenceLead?.current_step_id ?? null,
          status: cadenceLead?.status ?? null,
          cadence_memberships: memberships,
        } as LeadWithMemberships
      })
    },
    enabled: !!user && !!orgId,
  })

  const { data: templates = [] } = useQuery({
    queryKey: ['templates', orgId, user?.id],
    queryFn: async () => {
      if (!user || !orgId) return []
      const { data, error } = await supabase
        .from('templates')
        .select('*')
        .eq('org_id', orgId)
        .eq('owner_id', user.id)
        .order('created_at', { ascending: false })
      if (error) throw error
      return (data || []) as Template[]
    },
    enabled: !!user && !!orgId,
  })

  const createCadenceMutation = useMutation({
    mutationFn: async ({ name, description, automationMode }: { name: string; description?: string; automationMode?: 'manual' | 'automated' }) => {
      if (!user || !orgId) throw new Error('Not authenticated')

      // Ensure profile exists before creating cadence
      await ensureProfileExists(user.id)

      // automation_mode defaults to 'manual' (safe: nothing auto-schedules until
      // the user explicitly starts automation). Persist the caller's choice so a
      // cadence created in 'automated' mode is honored by process-queue's
      // advanceLeadToNextStep (which reads cadences.automation_mode). NOTE: even
      // in 'automated' mode nothing sends until the org-level outreach engine is
      // turned on (see /settings/outreach) — this flag only governs scheduling.
      const { data, error } = await supabase
        .from('cadences')
        .insert({
          name,
          description: description || null,
          owner_id: user.id,
          org_id: orgId,
          status: 'draft',
          automation_mode: automationMode ?? 'manual',
        })
        .select()
        .single()
      if (error) throw error
      return data as Cadence
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['cadences'] }),
  })

  const duplicateCadenceMutation = useMutation({
    mutationFn: async (sourceId: string) => {
      if (!user || !orgId) throw new Error('Not authenticated')

      // Load the source cadence with its steps
      const { data: source, error: srcErr } = await supabase
        .from('cadences')
        .select('*, cadence_steps(*)')
        .eq('id', sourceId)
        .single()
      if (srcErr || !source) throw new Error('Source cadence not found')

      // Build deduplicated name: strip existing "(N)" suffix then find next available number
      const baseName = source.name.replace(/\s*\(\d+\)$/, '')
      const existingNames = new Set(cadences.map((c) => c.name))
      let n = 1
      while (existingNames.has(n === 1 ? `${baseName} (1)` : `${baseName} (${n})`)) n++
      const newName = `${baseName} (${n})`

      // Create the new cadence (draft, no active leads)
      await ensureProfileExists(user.id)
      const { data: newCadence, error: cadErr } = await supabase
        .from('cadences')
        .insert({
          name: newName,
          description: source.description,
          owner_id: user.id,
          org_id: orgId,
          status: 'draft',
          automation_mode: source.automation_mode,
          same_day_delay_hours: source.same_day_delay_hours,
          timezone: source.timezone,
        })
        .select()
        .single()
      if (cadErr || !newCadence) throw new Error('Failed to create duplicate cadence')

      // Copy all steps
      const steps: CadenceStep[] = source.cadence_steps || []
      if (steps.length > 0) {
        const stepInserts = steps.map((s) => ({
          cadence_id: newCadence.id,
          owner_id: user.id,
          org_id: orgId,
          step_type: s.step_type,
          step_label: s.step_label,
          day_offset: s.day_offset,
          order_in_day: s.order_in_day,
          config_json: s.config_json,
        }))
        const { error: stepsErr } = await supabase.from('cadence_steps').insert(stepInserts)
        if (stepsErr) throw new Error('Failed to copy steps')
      }

      // Clone the POLICY layer (Carlos rubric, touch angles, circuit-breaker
      // thresholds) from the source onto the new cadence via the service-role
      // edge fn — those tables are RLS-off / service-role-write only and are
      // keyed by cadence_id (mig 172 / 107), so without this the duplicate would
      // silently fall back to the org-global defaults instead of the source's
      // effective policy. Best-effort by design: the cadence + steps are already
      // committed above, so a policy-clone failure must NOT invalidate the
      // duplicate (back-compat with every existing duplicateCadence caller) — we
      // warn and still return the new cadence.
      let policyCloned = true
      try {
        await cloneCadenceConfig({
          orgId,
          sourceCadenceId: sourceId,
          targetCadenceId: newCadence.id,
        })
      } catch (cloneErr) {
        policyCloned = false
        console.warn(
          `duplicateCadence: policy config clone failed for ${newCadence.id} (steps copied OK):`,
          cloneErr instanceof Error ? cloneErr.message : cloneErr,
        )
      }

      // Carry policyCloned back so the caller can toast a warning when the
      // best-effort policy clone silently failed (the cadence itself is fine).
      return { ...(newCadence as Cadence), policyCloned }
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['cadences'] }),
  })

  const updateCadenceMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<Cadence> }) => {
      const { error } = await supabase.from('cadences').update(data).eq('id', id)
      if (error) throw error
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['cadences'] }),
  })

  const deleteCadenceMutation = useMutation({
    mutationFn: async (id: string) => {
      // Delegate to edge function which uses service role key to bypass RLS,
      // validates ownership explicitly and cancels the cadence's pending
      // schedules. invokeEdgeOrThrow refreshes the JWT if it's near expiry —
      // the cached session token from AuthContext can be stale after long idle.
      await invokeEdgeOrThrow('delete-cadence', { cadenceId: id })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cadences'] })
      // Live schedule query keys (prefix match covers the id/orgId segments):
      // ['cadence-schedules', id] (CadenceBuilder) and ['outreach-scheduled', orgId]
      // (OutreachActivity). No query in the repo uses a bare ['schedules'] key.
      queryClient.invalidateQueries({ queryKey: ['cadence-schedules'] })
      queryClient.invalidateQueries({ queryKey: ['outreach-scheduled'] })
    },
  })

  const createStepMutation = useMutation({
    mutationFn: async (step: Omit<CadenceStep, 'id' | 'created_at' | 'updated_at' | 'org_id'>) => {
      if (!user || !orgId) throw new Error('Not authenticated')

      // Ensure owner_id and org_id are set
      const stepData = {
        ...step,
        owner_id: user.id,
        org_id: orgId,
      }

      const { data, error } = await supabase.from('cadence_steps').insert(stepData).select().single()
      if (error) throw error
      return data as CadenceStep
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['cadences'] }),
  })

  const updateStepMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<CadenceStep> }) => {
      const { error } = await supabase.from('cadence_steps').update(data).eq('id', id)
      if (error) throw error
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['cadences'] }),
  })

  const deleteStepMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('cadence_steps').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['cadences'] }),
  })

  const createLeadMutation = useMutation({
    mutationFn: async (lead: Omit<Lead, 'id' | 'created_at' | 'updated_at' | 'org_id'>) => {
      // Ensure profile exists before creating lead
      await ensureProfileExists(lead.owner_id)

      // Extract only the fields that exist in the leads table
      // (cadence_id, current_step_id, status are in cadence_leads table, not leads)
      const leadData = {
        owner_id: lead.owner_id,
        org_id: orgId!,
        first_name: lead.first_name,
        last_name: lead.last_name,
        email: lead.email,
        linkedin_url: lead.linkedin_url,
        company: lead.company,
        title: lead.title,
        phone: lead.phone,
        timezone: lead.timezone,
      }

      const { data, error } = await supabase.from('leads').insert(leadData).select().single()
      if (error) throw error
      return data as Lead
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['leads'] }),
  })

  const updateLeadMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<Lead> }) => {
      // Extract only the fields that exist in the leads table
      // (cadence_id, current_step_id, status are in cadence_leads table, not leads)
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { cadence_id, current_step_id, status, user_id, ...leadData } = data
      const { error } = await supabase.from('leads').update(leadData).eq('id', id)
      if (error) throw error
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['leads'] }),
  })

  const deleteLeadMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('leads').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['leads'] }),
  })

  const createTemplateMutation = useMutation({
    mutationFn: async (template: { name: string; step_type: string; subject_template?: string | null; body_template: string }) => {
      if (!user || !orgId) throw new Error('Not authenticated')
      const { data, error } = await supabase
        .from('templates')
        .insert({
          owner_id: user.id,
          org_id: orgId,
          name: template.name,
          step_type: template.step_type,
          subject_template: template.subject_template || null,
          body_template: template.body_template,
        })
        .select()
        .single()
      if (error) throw error
      return data as Template
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['templates'] }),
  })

  const updateTemplateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<Template> }) => {
      const { error } = await supabase.from('templates').update(data).eq('id', id)
      if (error) throw error
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['templates'] }),
  })

  const deleteTemplateMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('templates').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['templates'] }),
  })

  return (
    <CadenceContext.Provider
      value={{
        cadences,
        leads,
        templates,
        isLoading: cadencesLoading || leadsLoading,
        createCadence: async (name, description, automationMode) => createCadenceMutation.mutateAsync({ name, description, automationMode }),
        duplicateCadence: async (id) => duplicateCadenceMutation.mutateAsync(id),
        updateCadence: async (id, data) => updateCadenceMutation.mutateAsync({ id, data }),
        deleteCadence: async (id) => deleteCadenceMutation.mutateAsync(id),
        createStep: async (step) => createStepMutation.mutateAsync(step),
        updateStep: async (id, data) => updateStepMutation.mutateAsync({ id, data }),
        deleteStep: async (id) => deleteStepMutation.mutateAsync(id),
        createLead: async (lead) => createLeadMutation.mutateAsync(lead),
        updateLead: async (id, data) => updateLeadMutation.mutateAsync({ id, data }),
        deleteLead: async (id) => deleteLeadMutation.mutateAsync(id),
        assignLeadToCadence: async (leadId, cadenceId, startingStepId) => {
          if (!user || !orgId) throw new Error('Not authenticated')

          // Membership guard: never clobber LIVE progress. The pre-BUG-067
          // unconditional upsert reset current_step_id back to step 1 (re-outreach
          // from scratch) and silently flipped 'paused' leads (user protection or
          // reply auto-pause) back to 'active', re-arming their pending schedules.
          // BUG-093: but a membership that was REMOVED ('Quitar de cadencia' →
          // paused + current_step_id null, see removeLeadFromCadence) or
          // COMPLETED is NOT live progress — blanket-no-op'ing those made them
          // irrecoverable from the UI (/leads' Assign showed a false 'progreso
          // conservado' toast, and CadenceBuilder.handleMoveLeads deleted the
          // source membership while the destination stayed inert). Those are
          // explicitly REACTIVATED below.
          const { data: existing, error: existingErr } = await supabase
            .from('cadence_leads')
            .select('id, status, current_step_id')
            .eq('lead_id', leadId)
            .eq('cadence_id', cadenceId)
            .maybeSingle()

          if (existingErr) throw existingErr

          // Find the first step if no starting step is provided
          const cadence = cadences.find((c) => c.id === cadenceId)
          const sortedSteps = [...(cadence?.steps || [])].sort((a, b) => {
            if (a.day_offset !== b.day_offset) return a.day_offset - b.day_offset
            return a.order_in_day - b.order_in_day
          })
          const firstStepId = startingStepId || sortedSteps[0]?.id || null

          if (existing) {
            const isRemoved = existing.status === 'paused' && !existing.current_step_id
            const isCompleted = existing.status === 'completed'
            if (!isRemoved && !isCompleted) {
              // Live progress (active/scheduled, or paused mid-step) — preserve
              // as-is (original BUG-067 intent).
              return { alreadyInCadence: true, reactivated: false }
            }

            // Reactivation restarts at the first step this lead was NOT already
            // sent — for a fresh removal that IS step 1. It can't blindly point
            // at step 1: process-queue terminal-skips (without advancing) any
            // schedule whose lead_step_instance is 'sent' or that has a prior
            // 'executed' schedule row, so a reactivated lead pointed at an
            // already-sent step would strand 'active' forever. If EVERY step was
            // already sent, there is nothing left to re-send — keep the
            // membership as-is and report it as existing progress.
            const { data: sentInstances, error: sentErr } = await supabase
              .from('lead_step_instances')
              .select('cadence_step_id')
              .eq('lead_id', leadId)
              .eq('cadence_id', cadenceId)
              .eq('status', 'sent')

            if (sentErr) throw sentErr

            const sentStepIds = new Set((sentInstances || []).map((r) => r.cadence_step_id))
            // Restart at the TAIL: the step right after the LAST sent one — not
            // at an intermediate gap. Histories with gaps ('skipped' comment/like
            // steps, QA rejects) would crash the chain one step later: process-
            // queue's advanceLeadToNextStep degrades the next step's 'sent'
            // instance to 'pending' and either dead-ends (prior 'executed'
            // schedule row blocks a new schedule) or re-sends a touch that
            // already went out manually (no 'executed' row to block it).
            const lastSentIndex = sortedSteps.reduce(
              (acc, s, i) => (sentStepIds.has(s.id) ? i : acc),
              -1
            )
            const restartStep = sortedSteps[lastSentIndex + 1]

            if (!restartStep) {
              return { alreadyInCadence: true, reactivated: false }
            }

            const { error: reactivateErr } = await supabase
              .from('cadence_leads')
              .update({ status: 'active', current_step_id: restartStep.id })
              .eq('id', existing.id)

            if (reactivateErr) throw reactivateErr

            // Same schedule guarantee as a fresh assign: automated cadences are
            // driven ONLY by schedules — reactivating the membership without a
            // schedule row would be a silent dead end.
            if (cadence?.automation_mode === 'automated') {
              const { error: schedErr } = await supabase.from('schedules').insert({
                cadence_id: cadenceId,
                cadence_step_id: restartStep.id,
                lead_id: leadId,
                owner_id: user.id,
                org_id: orgId,
                scheduled_at: new Date(Date.now() + 60_000).toISOString(),
                timezone: 'UTC',
                status: 'scheduled',
              })

              // 23505 = partial unique index idx_schedules_active_lead_step
              // (mig 047): a live schedule for this (step, lead) already exists.
              if (schedErr && schedErr.code !== '23505') throw schedErr
            }

            queryClient.invalidateQueries({ queryKey: ['leads'] })
            queryClient.invalidateQueries({ queryKey: ['cadence-leads-direct'] })
            queryClient.invalidateQueries({ queryKey: ['cadence-schedules'] })
            queryClient.invalidateQueries({ queryKey: ['outreach-scheduled'] })

            return { alreadyInCadence: false, reactivated: true }
          }

          // Insert in cadence_leads — ignoreDuplicates makes the race with a
          // concurrent assign a no-op instead of overwriting the winner's row
          const { error } = await supabase
            .from('cadence_leads')
            .upsert({
              lead_id: leadId,
              cadence_id: cadenceId,
              owner_id: user.id,
              org_id: orgId,
              current_step_id: firstStepId,
              status: 'active',
            }, {
              onConflict: 'lead_id,cadence_id',
              ignoreDuplicates: true,
            })

          if (error) throw error

          // Automated cadences are driven ONLY by schedules: process-queue never
          // looks at cadence_leads alone, so a membership without a schedule row
          // is a silent dead end (the lead sits on step 1 forever). Create the
          // first step's schedule here — same row shape as the resume path above
          // and StartAutomationDialog. advanceLeadToNextStep re-creates each
          // following step after execution, so step 1 is all we need. This also
          // covers CadenceBuilder.handleMoveLeads (it assigns via this function),
          // so moving leads into an automated cadence no longer strands them.
          if (cadence?.automation_mode === 'automated' && firstStepId) {
            const { error: schedErr } = await supabase.from('schedules').insert({
              cadence_id: cadenceId,
              cadence_step_id: firstStepId,
              lead_id: leadId,
              owner_id: user.id,
              org_id: orgId,
              scheduled_at: new Date(Date.now() + 60_000).toISOString(),
              timezone: 'UTC',
              status: 'scheduled',
            })

            // 23505 = partial unique index idx_schedules_active_lead_step (mig 047):
            // a concurrent assign already created this (step, lead) schedule — done.
            if (schedErr && schedErr.code !== '23505') throw schedErr
          }

          // Invalidate queries to refresh data
          queryClient.invalidateQueries({ queryKey: ['leads'] })
          queryClient.invalidateQueries({ queryKey: ['cadence-leads-direct'] })
          queryClient.invalidateQueries({ queryKey: ['cadence-schedules'] })
          queryClient.invalidateQueries({ queryKey: ['outreach-scheduled'] })

          return { alreadyInCadence: false, reactivated: false }
        },
        removeLeadFromCadence: async (leadId, cadenceId) => {
          // Update status in cadence_leads table — scoped to ONE cadence so a lead
          // in multiple cadences keeps its progress in the others
          const { error } = await supabase
            .from('cadence_leads')
            .update({ status: 'paused', current_step_id: null })
            .eq('lead_id', leadId)
            .eq('cadence_id', cadenceId)

          if (error) throw error

          // Taking a lead out is permanent — also cancel its pre-created schedules
          // so automated cadences actually stop (same pattern as check-replies and
          // CadenceBuilder.handleMoveLeads). process-queue's membership gate is the
          // backstop if this update fails after the pause above succeeded.
          const { error: cancelError } = await supabase
            .from('schedules')
            .update({ status: 'canceled', last_error: 'Lead removed from cadence', updated_at: new Date().toISOString() })
            .eq('lead_id', leadId)
            .eq('cadence_id', cadenceId)
            .eq('status', 'scheduled')

          if (cancelError) throw cancelError

          queryClient.invalidateQueries({ queryKey: ['leads'] })
          queryClient.invalidateQueries({ queryKey: ['cadence-leads-direct'] })
        },
        updateLeadCadenceStatus: async (leadId, cadenceId, status) => {
          // Pause/resume live in cadence_leads (the leads table has no status column).
          // Keeps current_step_id so the lead's progress in the cadence survives.
          // Pending schedules are NOT canceled on pause, but they don't reliably
          // survive it either: check-replies cancels ALL of the lead's pending
          // schedules when it auto-pauses on a reply, and process-queue's
          // membership gate terminal-skips any schedule that comes due while
          // status='paused'. Resume therefore rebuilds the calendar below.
          const { error } = await supabase
            .from('cadence_leads')
            .update({ status })
            .eq('lead_id', leadId)
            .eq('cadence_id', cadenceId)

          if (error) throw error

          // On resume of an automated cadence: if the lead has no live schedule
          // left in this cadence, flipping status alone is a silent no-op — no
          // future step would ever execute. Re-create the current step's schedule
          // (same row shape as StartAutomationDialog) so process-queue picks the
          // lead back up on its next tick.
          const cadence = cadences.find((c) => c.id === cadenceId)
          if (status === 'active' && cadence?.automation_mode === 'automated' && user && orgId) {
            const { data: membershipRow, error: memberErr } = await supabase
              .from('cadence_leads')
              .select('current_step_id')
              .eq('lead_id', leadId)
              .eq('cadence_id', cadenceId)
              .maybeSingle()

            if (memberErr) throw memberErr

            let stepIdToSchedule = membershipRow?.current_step_id ?? null

            if (stepIdToSchedule) {
              // Scope the live-schedule check to the CURRENT step, not the whole
              // cadence: if the pause outlived the current step's slot (gate
              // terminal-skipped it) while a LATER step's schedule survived, a
              // cadence-wide check would see the later row and silently drop the
              // current touch. advanceLeadToNextStep re-creates following steps
              // (with its own existence check), so only the current step's
              // schedule can be lost — that's the one we must rebuild.
              const { data: liveSchedules, error: liveErr } = await supabase
                .from('schedules')
                .select('id')
                .eq('lead_id', leadId)
                .eq('cadence_id', cadenceId)
                .eq('cadence_step_id', stepIdToSchedule)
                .in('status', ['scheduled', 'processing'])
                .limit(1)

              if (liveErr) throw liveErr

              if (liveSchedules && liveSchedules.length > 0) {
                // Current touch already has a live schedule — nothing to rebuild
                stepIdToSchedule = null
              } else {
                // If the current step was already sent (pause landed between the
                // send and advanceLeadToNextStep), re-scheduling it would be
                // terminal-skipped by process-queue's dedup WITHOUT advancing the
                // lead — target the next step instead, mirroring markStepDoneForLead.
                const { data: instance } = await supabase
                  .from('lead_step_instances')
                  .select('status')
                  .eq('cadence_step_id', stepIdToSchedule)
                  .eq('lead_id', leadId)
                  .maybeSingle()

                if (instance?.status === 'sent') {
                  const sortedSteps = [...(cadence.steps || [])].sort((a, b) => {
                    if (a.day_offset !== b.day_offset) return a.day_offset - b.day_offset
                    return a.order_in_day - b.order_in_day
                  })
                  const currentIndex = sortedSteps.findIndex((s) => s.id === stepIdToSchedule)
                  const nextStep = currentIndex >= 0 ? sortedSteps[currentIndex + 1] : undefined

                  if (nextStep) {
                    const { error: advanceErr } = await supabase
                      .from('cadence_leads')
                      .update({ current_step_id: nextStep.id })
                      .eq('lead_id', leadId)
                      .eq('cadence_id', cadenceId)

                    if (advanceErr) throw advanceErr
                    stepIdToSchedule = nextStep.id
                  } else {
                    // Last step already sent — the cadence is done for this lead
                    const { error: completeErr } = await supabase
                      .from('cadence_leads')
                      .update({ current_step_id: null, status: 'completed' })
                      .eq('lead_id', leadId)
                      .eq('cadence_id', cadenceId)

                    if (completeErr) throw completeErr
                    stepIdToSchedule = null
                  }
                }
              }

              if (stepIdToSchedule) {
                const { error: schedErr } = await supabase.from('schedules').insert({
                  cadence_id: cadenceId,
                  cadence_step_id: stepIdToSchedule,
                  lead_id: leadId,
                  owner_id: user.id,
                  org_id: orgId,
                  scheduled_at: new Date(Date.now() + 60_000).toISOString(),
                  timezone: 'UTC',
                  status: 'scheduled',
                })

                // 23505 = partial unique index idx_schedules_active_lead_step hit a
                // concurrent creation for this (step, lead) — resume already achieved.
                if (schedErr && schedErr.code !== '23505') throw schedErr
              }
            }
          }

          queryClient.invalidateQueries({ queryKey: ['leads'] })
          queryClient.invalidateQueries({ queryKey: ['cadence-leads-direct'] })
          queryClient.invalidateQueries({ queryKey: ['cadence-schedules'] })
          queryClient.invalidateQueries({ queryKey: ['outreach-scheduled'] })
        },
        executeStepForLead: async ({ leadId, stepId, cadenceId, message, subject, postUrl }): Promise<ExecuteStepResult> => {
          if (!session?.access_token) {
            throw new Error('Not authenticated')
          }

          const cadence = cadences.find((c) => c.id === cadenceId)
          const step = cadence?.steps?.find((s) => s.id === stepId)
          const lead = leads.find((l) => l.id === leadId)

          if (!step || !lead) {
            throw new Error('Step or lead not found')
          }

          let result: ExecuteStepResult = { success: true }

          // Execute based on step type
          switch (step.step_type) {
            case 'linkedin_message':
              await invokeEdgeOrThrow(
                'linkedin-send-message',
                { leadId, message: message || '' }
              )
              break
            case 'linkedin_connect': {
              const response = await invokeEdgeOrThrow<{ success: boolean; alreadyConnected?: boolean }>(
                'linkedin-send-connection',
                { leadId, message: message || undefined }
              )
              result = {
                success: true,
                alreadyConnected: response.alreadyConnected || false,
              }
              break
            }
            case 'linkedin_profile_view':
              await invokeEdgeOrThrow(
                'linkedin-view-profile',
                { leadId }
              )
              break
            case 'linkedin_like':
              await invokeEdgeOrThrow(
                'linkedin-like-post',
                { leadId, postUrl: postUrl || (step.config_json as Record<string, unknown>)?.postUrl || '' }
              )
              break
            case 'linkedin_comment':
              await invokeEdgeOrThrow(
                'linkedin-comment',
                { leadId, postUrl: postUrl || (step.config_json as Record<string, unknown>)?.postUrl || '', comment: message || '' }
              )
              break
            case 'send_email': {
              // Convert plain text line breaks to HTML for proper email formatting.
              // Mirrors process-queue's textToHtml: pass through if already HTML,
              // otherwise escape &, <, > FIRST so text like "<2%" or "AT&T" isn't
              // parsed as a tag and swallowed by the email client.
              const rawMessage = message || ''
              const htmlBody = /<[a-z][\s\S]*>/i.test(rawMessage)
                ? rawMessage
                : rawMessage
                    .replace(/&/g, '&amp;')
                    .replace(/</g, '&lt;')
                    .replace(/>/g, '&gt;')
                    .split('\n\n')
                    .map(paragraph => `<p>${paragraph.replace(/\n/g, '<br>')}</p>`)
                    .join('')
              // Resolve subject: explicit > step config > selected template's subject_template.
              // CadenceBuilder never passes an explicit subject, so without the template
              // fallback every manual send went out with the literal subject "No subject".
              const stepConfig = step.config_json as Record<string, unknown> | null
              let resolvedSubject = subject || (stepConfig?.subject as string | undefined) || ''
              if (!resolvedSubject.trim()) {
                const templateId = stepConfig?.template_id as string | undefined
                const subjectTemplate = templateId
                  ? templates.find((t) => t.id === templateId)?.subject_template
                  : null
                if (subjectTemplate) {
                  resolvedSubject = subjectTemplate
                    .replace(/\{\{first_name\}\}/g, lead.first_name || '')
                    .replace(/\{\{last_name\}\}/g, lead.last_name || '')
                    .replace(/\{\{company\}\}/g, lead.company || '')
                    .replace(/\{\{title\}\}/g, lead.title || '')
                    .replace(/\{\{email\}\}/g, lead.email || '')
                }
              }
              if (!resolvedSubject.trim()) {
                // Never email a prospect with a placeholder subject — fail loudly instead
                throw new Error('Email subject missing: agrega un subject al template de este step antes de enviar')
              }
              await invokeEdgeOrThrow(
                'send-email',
                { leadId, body: htmlBody, subject: resolvedSubject }
              )
              break
            }
            // Manual steps (whatsapp, cold_call, task) just get marked as done
            default:
              break
          }

          queryClient.invalidateQueries({ queryKey: ['leads'] })
          queryClient.invalidateQueries({ queryKey: ['cadence-leads-direct'] })
          queryClient.invalidateQueries({ queryKey: ['activity-log'] })

          return result
        },
        markStepDoneForLead: async (leadId, stepId, cadenceId) => {
          // Neutralize the automated pipeline for THIS (lead, step) FIRST.
          // Automated cadences pre-create one schedule per step
          // (StartAutomationDialog) — a manual send does not consume it, so
          // when scheduled_at arrives process-queue re-executes the step
          // (double touch) and advanceLeadToNextStep re-points current_step_id
          // backwards. Cancel the pending schedule (same pattern as
          // removeLeadFromCadence) and mark the lead_step_instance 'sent' —
          // process-queue's dedup skips any schedule whose instance is already
          // 'sent', which also covers rows the cancel can't reach (e.g. a
          // schedule re-created later by advanceLeadToNextStep).
          const { error: cancelError } = await supabase
            .from('schedules')
            .update({ status: 'canceled', last_error: 'Step executed manually', updated_at: new Date().toISOString() })
            .eq('lead_id', leadId)
            .eq('cadence_id', cadenceId)
            .eq('cadence_step_id', stepId)
            .eq('status', 'scheduled')

          if (cancelError) throw cancelError

          if (user && orgId) {
            const { error: instanceError } = await supabase
              .from('lead_step_instances')
              .upsert({
                cadence_id: cadenceId,
                cadence_step_id: stepId,
                lead_id: leadId,
                owner_id: user.id,
                org_id: orgId,
                status: 'sent',
              }, { onConflict: 'cadence_step_id,lead_id' })

            if (instanceError) throw instanceError
          }

          // Find the next step and move the lead
          const cadence = cadences.find((c) => c.id === cadenceId)
          const sortedSteps = [...(cadence?.steps || [])].sort((a, b) => {
            if (a.day_offset !== b.day_offset) return a.day_offset - b.day_offset
            return a.order_in_day - b.order_in_day
          })

          const currentIndex = sortedSteps.findIndex((s) => s.id === stepId)
          const nextStep = sortedSteps[currentIndex + 1]

          if (nextStep) {
            // Move to next step in cadence_leads table
            const { error } = await supabase
              .from('cadence_leads')
              .update({ current_step_id: nextStep.id })
              .eq('lead_id', leadId)
              .eq('cadence_id', cadenceId)

            if (error) throw error

            // Guarantee the NEW step has a live schedule. Chain-model leads
            // (assign / resume paths) carry ONE schedule at a time and rely on
            // process-queue's advanceLeadToNextStep to create the next one
            // AFTER executing the current schedule — but the cancel above just
            // consumed that only schedule, so advanceLeadToNextStep never runs
            // and the lead would sit 'active' on nextStep forever (silent dead
            // end). Insert the next step's schedule here, honoring the
            // day-offset gap in business days (same fallback rule as
            // process-queue). Calendar-model leads (StartAutomationDialog
            // pre-created every step) already have this row: the insert hits
            // the partial unique index idx_schedules_active_lead_step
            // (mig 047) → 23505 = schedule already live = success.
            if (cadence?.automation_mode === 'automated' && user && orgId) {
              const currentDayOffset = sortedSteps[currentIndex]?.day_offset ?? nextStep.day_offset
              const dayDiff = Math.max(0, nextStep.day_offset - currentDayOffset)
              const scheduleAt = new Date()
              if (dayDiff === 0) {
                scheduleAt.setMinutes(scheduleAt.getMinutes() + 60)
              } else {
                let remaining = dayDiff
                while (remaining > 0) {
                  scheduleAt.setDate(scheduleAt.getDate() + 1)
                  if (scheduleAt.getDay() !== 0 && scheduleAt.getDay() !== 6) remaining--
                }
              }

              const { error: schedErr } = await supabase.from('schedules').insert({
                cadence_id: cadenceId,
                cadence_step_id: nextStep.id,
                lead_id: leadId,
                owner_id: user.id,
                org_id: orgId,
                scheduled_at: scheduleAt.toISOString(),
                timezone: 'UTC',
                status: 'scheduled',
              })

              if (schedErr && schedErr.code !== '23505') throw schedErr
            }
          } else {
            // No more steps, mark as completed in cadence_leads table.
            // Guarded to live memberships (same pattern as process-queue's
            // advanceLeadToNextStep): if check-replies auto-paused the lead
            // between page load and this click, keep the pause — the reply
            // signal and reply metrics (migs 139/140) count status='paused'.
            // The instance is already 'sent' above, so a later resume
            // completes the membership through updateLeadCadenceStatus.
            const { error } = await supabase
              .from('cadence_leads')
              .update({ current_step_id: null, status: 'completed' })
              .eq('lead_id', leadId)
              .eq('cadence_id', cadenceId)
              .in('status', ['active', 'scheduled'])

            if (error) throw error
          }

          queryClient.invalidateQueries({ queryKey: ['leads'] })
          queryClient.invalidateQueries({ queryKey: ['cadence-leads-direct'] })
          // This function cancels + (for automated cadences) creates schedule
          // rows — refresh the live schedule views too (same keys as BUG-090:
          // ['cadence-schedules', id] in CadenceBuilder, ['outreach-scheduled',
          // orgId] in OutreachActivity).
          queryClient.invalidateQueries({ queryKey: ['cadence-schedules'] })
          queryClient.invalidateQueries({ queryKey: ['outreach-scheduled'] })
        },
        moveLeadToNextStep: async (leadId, cadenceId) => {
          const lead = leads.find((l) => l.id === leadId)
          if (!lead?.current_step_id) return

          const cadence = cadences.find((c) => c.id === cadenceId)
          const sortedSteps = [...(cadence?.steps || [])].sort((a, b) => {
            if (a.day_offset !== b.day_offset) return a.day_offset - b.day_offset
            return a.order_in_day - b.order_in_day
          })

          const currentIndex = sortedSteps.findIndex((s) => s.id === lead.current_step_id)
          const nextStep = sortedSteps[currentIndex + 1]

          if (nextStep) {
            const { error } = await supabase
              .from('cadence_leads')
              .update({ current_step_id: nextStep.id })
              .eq('lead_id', leadId)
              .eq('cadence_id', cadenceId)

            if (error) throw error
          } else {
            // Same anti-revive guard as markStepDoneForLead: never overwrite a
            // paused membership (reply auto-pause / user pause) with 'completed'
            const { error } = await supabase
              .from('cadence_leads')
              .update({ current_step_id: null, status: 'completed' })
              .eq('lead_id', leadId)
              .eq('cadence_id', cadenceId)
              .in('status', ['active', 'scheduled'])

            if (error) throw error
          }

          queryClient.invalidateQueries({ queryKey: ['leads'] })
          queryClient.invalidateQueries({ queryKey: ['cadence-leads-direct'] })
        },
        getLeadDayInCadence: (lead) => {
          if (!lead.created_at) return 0
          const createdAt = new Date(lead.created_at)
          const now = new Date()
          const diffTime = Math.abs(now.getTime() - createdAt.getTime())
          const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24))
          return diffDays
        },
        createTemplate: async (template) => createTemplateMutation.mutateAsync(template),
        updateTemplate: async (id, data) => updateTemplateMutation.mutateAsync({ id, data }),
        deleteTemplate: async (id) => deleteTemplateMutation.mutateAsync(id),
      }}
    >
      {children}
    </CadenceContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export function useCadence() {
  const context = useContext(CadenceContext)
  if (context === undefined) {
    throw new Error('useCadence must be used within a CadenceProvider')
  }
  return context
}
